import { ChangeEvent } from 'react';
import {
  FunnelIcon,
  ListBulletIcon,
  MagnifyingGlassIcon,
  RectangleGroupIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { Checkbox } from '@tailwindComponents/Checkbox';

export type SkuViewMode = 'products' | 'list' | 'grid';

export interface SkuToolbarProps {
  searchQuery: string;
  onSearchQueryChange: (event: ChangeEvent<HTMLInputElement>) => void;
  includeArchived: boolean;
  onIncludeArchivedChange: (value: boolean) => void;
  isRailVisible: boolean;
  onToggleRail: () => void;
  view: SkuViewMode;
  onViewChange: (view: SkuViewMode) => void;
}

export function SkuToolbar({
  searchQuery,
  onSearchQueryChange,
  includeArchived,
  onIncludeArchivedChange,
  isRailVisible,
  onToggleRail,
  view,
  onViewChange,
}: SkuToolbarProps) {
  const showShortQueryHint = searchQuery.length >= 1 && searchQuery.length < 4;

  const viewToggleClasses = (isActive: boolean) =>
    `rounded-md p-1.5 transition-colors ${
      isActive ? 'bg-blue-50 text-primary' : 'text-gray-400 hover:text-gray-600'
    }`;

  return (
    <div className='flex flex-col gap-1 font-inter'>
      <div className='flex flex-wrap items-center gap-3'>
        <div className='relative min-w-[16rem] flex-1 sm:max-w-xs'>
          <MagnifyingGlassIcon className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
          <input
            type='text'
            name='searchSKUs'
            aria-label='Search products'
            value={searchQuery}
            onChange={onSearchQueryChange}
            placeholder='Search products...'
            className='h-9 w-full rounded-lg border border-gray-300 pl-9 pr-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200'
          />
        </div>
        <button
          type='button'
          aria-pressed={isRailVisible}
          onClick={onToggleRail}
          className={`inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-sm font-medium transition-colors ${
            isRailVisible
              ? 'border-blue-200 bg-blue-50 text-primary'
              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <FunnelIcon className='h-4 w-4' />
          Filters
        </button>
        <Checkbox
          label='Include archived'
          checked={includeArchived}
          onChange={(e) => onIncludeArchivedChange(e.target.checked)}
        />
        <div className='ml-auto inline-flex rounded-lg border border-gray-200 p-0.5'>
          <button
            type='button'
            aria-label='Products view'
            aria-pressed={view === 'products'}
            onClick={() => onViewChange('products')}
            className={viewToggleClasses(view === 'products')}
          >
            <RectangleGroupIcon className='h-4 w-4' />
          </button>
          <button
            type='button'
            aria-label='List view'
            aria-pressed={view === 'list'}
            onClick={() => onViewChange('list')}
            className={viewToggleClasses(view === 'list')}
          >
            <ListBulletIcon className='h-4 w-4' />
          </button>
          <button
            type='button'
            aria-label='Grid view'
            aria-pressed={view === 'grid'}
            onClick={() => onViewChange('grid')}
            className={viewToggleClasses(view === 'grid')}
          >
            <Squares2X2Icon className='h-4 w-4' />
          </button>
        </div>
      </div>
      {showShortQueryHint && (
        <p className='text-xs text-gray-400'>
          Type at least 4 characters to search.
        </p>
      )}
    </div>
  );
}
